import apiClient from "@/api/client";
import type {
  DriverLoginRequest,
  LoginRequest,
  MeResponse,
  MessageResponse,
  PasswordChangeRequest,
  TokenResponse,
} from "@/types/domain";

/**
 * 인증 API 래퍼 (API-AUTH).
 *
 * 요청·응답 타입은 `backend/app/schemas/auth.py` 와 1:1로 맞춘다.
 * 토큰 저장·헤더 부착·401 재발급은 `api/client.ts` 인터셉터가 담당하므로
 * 여기서는 엔드포인트 호출과 타입만 책임진다.
 */

// ---------------------------------------------------------------------------
// 로그인
// ---------------------------------------------------------------------------

/** POST /api/auth/login — 담당자·시스템관리자 로그인 (API-AUTH-001) */
export async function login(payload: LoginRequest): Promise<TokenResponse> {
  const { data } = await apiClient.post<TokenResponse>("/auth/login", payload);
  return data;
}

/** POST /api/auth/driver-login — 기사 로그인 (API-AUTH-002) */
export async function driverLogin(payload: DriverLoginRequest): Promise<TokenResponse> {
  const { data } = await apiClient.post<TokenResponse>("/auth/driver-login", payload);
  return data;
}

// ---------------------------------------------------------------------------
// 토큰 · 세션
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/refresh — 토큰 갱신 (API-AUTH-003).
 * 만료 시 자동 갱신은 `client.ts` 인터셉터가 처리하므로 이 함수는 수동 갱신용이다.
 */
export async function refresh(refreshToken: string): Promise<TokenResponse> {
  const { data } = await apiClient.post<TokenResponse>("/auth/refresh", {
    refresh_token: refreshToken,
  });
  return data;
}

/**
 * POST /api/auth/logout — 로그아웃 (API-AUTH-004).
 * 서버는 감사 로그만 남기므로, 토큰 폐기는 호출측(AuthContext)이 반드시 수행한다.
 */
export async function logout(): Promise<MessageResponse> {
  const { data } = await apiClient.post<MessageResponse>("/auth/logout");
  return data;
}

/** GET /api/auth/me — 토큰 주체 정보 (API-AUTH-005). 관리자·기사 토큰 모두 허용 */
export async function getMe(): Promise<MeResponse> {
  const { data } = await apiClient.get<MeResponse>("/auth/me");
  return data;
}

// ---------------------------------------------------------------------------
// 비밀번호
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/password — 본인 비밀번호 변경 (API-AUTH-006).
 * `auth_source='1차'` 기사는 1차 시스템이 비밀번호를 보유하므로 변경할 수 없다.
 */
export async function changePassword(
  payload: PasswordChangeRequest
): Promise<MessageResponse> {
  const { data } = await apiClient.post<MessageResponse>("/auth/password", payload);
  return data;
}
